import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Icon, Input, SegmentedControl, Sheet, useToast } from "@xinghai/ui";
import { studentApi } from "../../lib/api";
import type { ProfilePayload } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";
import { TRAINING_LEVELS } from "../../lib/constants";
import type { TrainingLevel } from "../../lib/constants";
import { DialectPicker } from "../auth/DialectPicker";

export interface ProfileSheetProps {
  open: boolean;
  onClose: () => void;
  initial: ProfilePayload;
}

/**
 * docs/ROADMAP.md A1 — the background survey stays editable: a student who
 * mis-picked a dialect group would otherwise get the wrong task pack forever.
 */
export function ProfileSheet({ open, onClose, initial }: ProfileSheetProps) {
  const qc = useQueryClient();
  const toast = useToast();

  const [dialects, setDialects] = useState<string[]>(initial.dialect_l1);
  const [hometown, setHometown] = useState(initial.hometown ?? "");
  const [level, setLevel] = useState<string>(initial.training_level);
  const [contact, setContact] = useState(initial.contact ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDialects(initial.dialect_l1);
    setHometown(initial.hometown ?? "");
    setLevel(initial.training_level);
    setContact(initial.contact ?? "");
    setError(null);
  }, [open, initial]);

  const save = useMutation({
    mutationFn: () =>
      studentApi.updateProfile({
        dialect_l1: dialects,
        hometown: hometown.trim() || null,
        training_level: level,
        contact: contact.trim() || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: qk.profile });
      void qc.invalidateQueries({ queryKey: qk.assignments });
      toast.success("资料已更新。");
      onClose();
    },
    onError: (err) => setError(friendlyMessage(err, "没能保存，请稍后重试。")),
  });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="修改我的资料"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            取消
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => {
              if (dialects.length === 0) {
                setError("方言背景至少选一个。");
                return;
              }
              setError(null);
              save.mutate();
            }}
          >
            保存
          </Button>
        </>
      }
    >
      <p className="gz-muted">方言背景决定你会拿到哪些练习内容，填得准一点，任务会更合适。</p>

      <Field label="方言背景（可多选）">
        <DialectPicker value={dialects} onChange={setDialects} disabled={save.isPending} />
      </Field>
      <Field label="家乡" hint="可以写到市或县">
        <Input value={hometown} onChange={(e) => setHometown(e.target.value)} />
      </Field>
      <Field label="训练情况" hint={TRAINING_LEVELS.map((t) => `${t.label}：${t.hint}`).join("；")}>
        <SegmentedControl<TrainingLevel>
          label="训练情况"
          vertical
          options={TRAINING_LEVELS.map((t) => ({ value: t.value, label: t.label }))}
          value={(level as TrainingLevel) || null}
          onChange={(v) => setLevel(v)}
        />
      </Field>
      <Field label="联系方式" hint="只用于老师联系你，不会公开">
        <Input value={contact} onChange={(e) => setContact(e.target.value)} />
      </Field>

      {error && (
        <p className="gz-notice gz-notice--error" role="alert">
          <Icon id="ui-error" size={20} />
          {error}
        </p>
      )}
    </Sheet>
  );
}
